import React, { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CATEGORIES } from "../lib/categories";
import WhatsAppBroadcastModal from "../components/WhatsAppBroadcastModal";
import MediaEmbed, { detectMedia } from "../components/MediaEmbed";
import {
  Plus, Pencil, Trash2, X, UploadCloud, Image as ImageIcon, Send, FileText,
  Users, FolderTree, Settings, FileEdit, BarChart3, CheckCircle2, Clock, ChevronRight,
  MessageCircle, Rss, RefreshCw, Zap, Sparkles, FileUp, TrendingUp, Eye, Activity,
} from "lucide-react";

const STATUS_BADGE = {
  draft: "bg-zinc-100 text-zinc-700",
  submitted: "bg-amber-50 text-amber-700",
  scheduled: "bg-blue-50 text-blue-700",
  published: "bg-green-50 text-green-700",
  archived: "bg-zinc-200 text-zinc-500",
};
const STATUS_HI = {
  draft: "ड्राफ्ट", submitted: "समीक्षा हेतु", scheduled: "शेड्यूल्ड",
  published: "प्रकाशित", archived: "संग्रहित",
};

export default function NewsroomDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("articles");
  if (loading) return <div className="p-10 text-center font-devanagari">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (!["admin", "editor", "reporter"].includes(user.role))
    return <div className="p-10 text-center font-devanagari">केवल न्यूजरूम स्टाफ एक्सेस.</div>;

  const TABS = [
    { key: "overview", label: "ओवरव्यू", icon: BarChart3, roles: ["admin", "editor", "reporter"] },
    { key: "analytics", label: "एनालिटिक्स", icon: TrendingUp, roles: ["admin", "editor"] },
    { key: "articles", label: "खबरें", icon: FileText, roles: ["admin", "editor", "reporter"] },
    { key: "media", label: "मीडिया", icon: ImageIcon, roles: ["admin", "editor", "reporter"] },
    { key: "categories", label: "श्रेणियाँ", icon: FolderTree, roles: ["admin", "editor"] },
    { key: "pages", label: "पेज", icon: FileEdit, roles: ["admin", "editor"] },
    { key: "users", label: "उपयोगकर्ता", icon: Users, roles: ["admin"] },
    { key: "sources", label: "वायर सोर्स", icon: Rss, roles: ["admin"] },
    { key: "settings", label: "सेटिंग्स", icon: Settings, roles: ["admin"] },
  ].filter((t) => t.roles.includes(user.role));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="newsroom-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-zinc-100 pb-6 mb-6">
        <div>
          <div className="font-outfit font-black uppercase tracking-widest text-xs bodh-text-gradient">{user.role.toUpperCase()} • NEWSROOM</div>
          <h1 className="mt-1 font-devanagari font-black text-4xl text-zinc-900">डैशबोर्ड</h1>
          <p className="mt-1 text-zinc-500 font-hind">स्वागत है, {user.name}</p>
        </div>
        <Link to="/" className="text-sm font-outfit font-bold bodh-link">← वेबसाइट देखें</Link>
      </div>

      <RoleHelpBanner role={user.role} />

      <div className="flex flex-wrap gap-2 mb-8 border-b border-zinc-100">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-4 py-2.5 -mb-px border-b-2 font-outfit font-bold text-sm inline-flex items-center gap-1.5 transition ${
              tab === t.key ? "border-[#E30613] text-[#E30613]" : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab />}
      {tab === "analytics" && <AnalyticsTab />}
      {tab === "articles" && <ArticlesTab user={user} />}
      {tab === "media" && <MediaTab />}
      {tab === "categories" && <CategoriesTab />}
      {tab === "pages" && <PagesTab />}
      {tab === "users" && <UsersTab />}
      {tab === "sources" && <SourcesTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

// ============ HELP BANNER ============
function RoleHelpBanner({ role }) {
  const map = {
    reporter: {
      title: "रिपोर्टर गाइड",
      steps: [
        "खबरें टैब में 'नई खबर' पर क्लिक करें",
        "शीर्षक, सारांश, विषयवस्तु लिखें - लेखक का नाम और श्रेणी चुनें",
        "कवर इमेज अपलोड करें या URL पेस्ट करें",
        "वीडियो/Instagram/Facebook/Spotify लिंक पेस्ट करें - खुद embed हो जाएगा",
        "स्थिति 'समीक्षा हेतु' पर सेट कर सेव करें - एडिटर अप्रूव करेंगे",
      ],
    },
    editor: {
      title: "एडिटर गाइड",
      steps: [
        "सबमिटेड फिल्टर पर क्लिक कर रिव्यू पेंडिंग खबरें देखें",
        "किसी भी खबर को एडिट करें या Publish बटन दबाकर सीधे प्रकाशित करें",
        "Scheduled विकल्प से भविष्य के लिए शेड्यूल करें",
        "श्रेणी, पेज (About/Contact आदि), मीडिया लाइब्रेरी - सब मैनेज कर सकते हैं",
        "WhatsApp Broadcast (WA बटन) से सब्सक्राइबर्स को भेजें",
      ],
    },
    admin: {
      title: "एडमिन गाइड",
      steps: [
        "उपयोगकर्ता टैब से नए रिपोर्टर/एडिटर अकाउंट बनाएं (पासवर्ड सेट करें और उन्हें ईमेल से शेयर करें)",
        "सेटिंग्स टैब से सोशल हैंडल, संपर्क, YouTube Live ID अपडेट करें",
        "श्रेणियों को जोड़/हटा सकते हैं",
        "पूरे एडिटोरियल वर्कफ्लो को मैनेज करें",
      ],
    },
  };
  const c = map[role];
  if (!c) return null;
  return (
    <div className="mb-6 rounded-2xl bg-gradient-to-r from-amber-50 via-white to-rose-50 border border-amber-100 p-5" data-testid="role-help-banner">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bodh-gradient flex items-center justify-center text-white shrink-0">
          <BarChart3 className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-devanagari font-black text-lg text-zinc-900">{c.title}</h3>
          <ol className="mt-2 list-decimal pl-5 space-y-1 font-devanagari text-sm text-zinc-700">
            {c.steps.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      </div>
    </div>
  );
}

// ============ OVERVIEW ============
function OverviewTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="font-devanagari text-zinc-500">Loading...</div>;
  const cards = [
    { k: "total_articles", l: "कुल खबरें", c: "from-zinc-900 to-zinc-700" },
    { k: "published", l: "प्रकाशित", c: "from-green-600 to-emerald-500" },
    { k: "draft", l: "ड्राफ्ट", c: "from-zinc-500 to-zinc-400" },
    { k: "submitted", l: "समीक्षा हेतु", c: "from-amber-500 to-orange-500" },
    { k: "scheduled", l: "शेड्यूल्ड", c: "from-blue-600 to-indigo-500" },
    { k: "archived", l: "संग्रहित", c: "from-zinc-400 to-zinc-300" },
    { k: "users", l: "उपयोगकर्ता", c: "from-[#E30613] to-[#F4B400]" },
    { k: "newsletter", l: "न्यूजलेटर", c: "from-rose-500 to-pink-500" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="overview-grid">
      {cards.map((c) => (
        <div key={c.k} data-testid={`stat-${c.k}`} className={`rounded-2xl p-5 text-white bg-gradient-to-br ${c.c} shadow-sm`}>
          <div className="font-outfit font-black text-3xl">{s[c.k] ?? 0}</div>
          <div className="mt-1 font-devanagari text-sm opacity-90">{c.l}</div>
        </div>
      ))}
    </div>
  );
}

// ============ ARTICLES ============
const EMPTY_ARTICLE = {
  title: "", subtitle: "", summary: "", content: "", category: "national",
  cover_image: "", author: "", tags: "",
  is_breaking: false, is_trending: false, is_featured: false,
  is_short: false, is_video: false, video_url: "", audio_url: "", reading_time: 3,
  status: "draft", publish_at: "", priority: 0,
  meta_title: "", meta_description: "", focus_keyword: "", og_image: "", slug_override: "",
};

function ArticlesTab({ user }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_ARTICLE);
  const [broadcastArticle, setBroadcastArticle] = useState(null);

  const isReporter = user.role === "reporter";

  const refresh = async () => {
    const params = filter === "all" ? {} : { status: filter };
    const { data } = await api.get("/newsroom/articles", { params });
    setItems(data || []);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const startNew = () => { setEditing(null); setForm(EMPTY_ARTICLE); setShowForm(true); };
  const startEdit = (a) => {
    setEditing(a.id);
    setForm({
      ...EMPTY_ARTICLE, ...a,
      tags: (a.tags || []).join(", "),
      publish_at: a.publish_at ? a.publish_at.slice(0, 16) : "",
    });
    setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditing(null); setForm(EMPTY_ARTICLE); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      tags: String(form.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
      reading_time: Number(form.reading_time) || 3,
      priority: Number(form.priority) || 0,
      publish_at: form.status === "scheduled" && form.publish_at
        ? new Date(form.publish_at).toISOString() : null,
    };
    ["id", "slug", "views", "created_at", "updated_at", "published_at", "author_id"].forEach((k) => delete payload[k]);
    try {
      if (editing) await api.put(`/articles/${editing}`, payload);
      else await api.post("/articles", payload);
      toast.success(editing ? "अपडेट हुआ" : "सेव हुआ");
      cancel(); refresh();
    } catch (e) {
      toast.error("त्रुटि: " + (e?.response?.data?.detail || "save failed"));
    }
  };

  const del = async (id) => {
    if (!window.confirm("हटाना चाहते हैं?")) return;
    try { await api.delete(`/articles/${id}`); toast.success("हटाया गया"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "त्रुटि"); }
  };

  const publish = async (id) => {
    try { await api.post(`/newsroom/articles/${id}/publish`); toast.success("प्रकाशित किया गया"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-1 flex-wrap" data-testid="status-filter">
          {["all", "draft", "submitted", "scheduled", "published", "archived"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-outfit font-bold transition ${
                filter === s ? "bodh-gradient text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
              }`}>
              {s === "all" ? "सभी" : STATUS_HI[s]}
            </button>
          ))}
        </div>
        <button onClick={startNew} data-testid="new-article-btn" className="inline-flex items-center gap-1.5 bodh-gradient text-white font-outfit font-bold px-5 py-2.5 rounded-full">
          <Plus className="h-4 w-4" /> नई खबर
        </button>
      </div>

      {showForm && (
        <ArticleForm form={form} setForm={setForm} editing={editing} isReporter={isReporter} onCancel={cancel} onSubmit={submit} />
      )}

      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-100">
            <tr className="text-left font-outfit text-xs uppercase tracking-widest text-zinc-500">
              <th className="px-4 py-3">शीर्षक</th>
              <th className="px-4 py-3">श्रेणी</th>
              <th className="px-4 py-3">स्थिति</th>
              <th className="px-4 py-3">अपडेट</th>
              <th className="px-4 py-3 text-right">क्रिया</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} data-testid={`article-row-${a.id}`} className="border-b border-zinc-50 hover:bg-zinc-50/50">
                <td className="px-4 py-3 font-devanagari font-semibold max-w-md truncate">{a.title}</td>
                <td className="px-4 py-3 font-devanagari text-zinc-600">{CATEGORIES.find((c) => c.slug === a.category)?.name_hi || a.category}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${STATUS_BADGE[a.status] || ""}`}>{STATUS_HI[a.status] || a.status}</span>
                  {a.status === "scheduled" && a.publish_at && (
                    <span className="ml-2 text-xs text-zinc-500"><Clock className="h-3 w-3 inline" /> {new Date(a.publish_at).toLocaleString("hi-IN")}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-zinc-500">{a.updated_at ? new Date(a.updated_at).toLocaleString("hi-IN") : "-"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {a.status === "published" && (
                    <button onClick={() => setBroadcastArticle(a)} data-testid={`wa-${a.id}`} className="inline-flex items-center gap-1 mr-3 text-xs font-outfit font-bold text-emerald-700 hover:text-emerald-900" title="WhatsApp Broadcast"><MessageCircle className="h-3.5 w-3.5" /> WA</button>
                  )}
                  {!isReporter && a.status !== "published" && (
                    <button onClick={() => publish(a.id)} data-testid={`publish-${a.id}`} className="inline-flex items-center gap-1 mr-3 text-xs font-outfit font-bold text-green-700 hover:text-green-900"><Send className="h-3.5 w-3.5" /> Publish</button>
                  )}
                  <button onClick={() => startEdit(a)} data-testid={`edit-${a.id}`} className="inline-flex items-center mr-2 text-zinc-700 hover:text-[#E30613]"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(a.id)} data-testid={`del-${a.id}`} className="inline-flex items-center text-zinc-700 hover:text-[#E30613]"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center font-devanagari text-zinc-400">कोई खबर नहीं मिली</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <WhatsAppBroadcastModal article={broadcastArticle} open={!!broadcastArticle} onClose={() => setBroadcastArticle(null)} />
    </div>
  );
}

function ArticleForm({ form, setForm, editing, isReporter, onCancel, onSubmit }) {
  const fileRef = useRef();
  const [rewriting, setRewriting] = useState(false);

  const aiRewrite = async () => {
    if (!editing) return;
    if (!window.confirm("AI से दोबारा लिखवाएँ? वर्तमान फॉर्म की content/title/subtitle/summary बदल जाएगी (सेव करने तक स्थायी नहीं है).")) return;
    setRewriting(true);
    try {
      const { data } = await api.post(`/admin/articles/${editing}/ai-rewrite`, { instructions: "" });
      if (data.ok && data.rewritten) {
        setForm((f) => ({
          ...f,
          title: data.rewritten.title || f.title,
          subtitle: data.rewritten.subtitle || f.subtitle,
          summary: data.rewritten.summary || f.summary,
          content: data.rewritten.content || f.content,
        }));
        toast.success("✨ Bodh शैली में दोबारा लिखा गया. समीक्षा करें और सेव करें.");
      } else {
        toast.error("AI rewrite failed");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      if (typeof msg === "string" && msg.toLowerCase().includes("budget")) {
        toast.error("Emergent LLM key का बजट कम है. Profile → Universal Key → Add Balance से टॉप अप करें.");
      } else {
        toast.error("AI rewrite error: " + msg);
      }
    } finally {
      setRewriting(false);
    }
  };

  const uploadImage = async (file, field = "cover_image") => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, [field]: url }));
      toast.success("इमेज अपलोड हुई");
    } catch (e) {
      toast.error("अपलोड विफल");
    }
  };

  const statusOptions = isReporter
    ? [["draft", "ड्राफ्ट"], ["submitted", "समीक्षा हेतु जमा करें"]]
    : [["draft", "ड्राफ्ट"], ["submitted", "समीक्षा हेतु"], ["scheduled", "शेड्यूल"], ["published", "प्रकाशित"], ["archived", "संग्रहित"]];

  return (
    <form onSubmit={onSubmit} className="mb-8 bg-white rounded-2xl border border-zinc-100 p-6 shadow-sm" data-testid="article-form">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h2 className="font-devanagari font-black text-xl">{editing ? "खबर संपादित करें" : "नई खबर"}</h2>
        <div className="flex items-center gap-2">
          {editing && (
            <button
              type="button"
              onClick={aiRewrite}
              disabled={rewriting}
              data-testid="ai-rewrite-btn"
              title="Bodh शैली में दोबारा लिखें (Claude Sonnet 4.5)"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-outfit font-bold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm hover:scale-[1.02] disabled:opacity-50 transition"
            >
              {rewriting ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> AI लिख रहा है</> : <><Sparkles className="h-3.5 w-3.5" /> Bodh शैली में लिखें</>}
            </button>
          )}
          <button type="button" onClick={onCancel} className="p-2 rounded-full hover:bg-zinc-100"><X className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <F label="शीर्षक" required full value={form.title} on={(v) => setForm({ ...form, title: v })} tid="f-title" />
        <F label="उपशीर्षक" full value={form.subtitle} on={(v) => setForm({ ...form, subtitle: v })} tid="f-subtitle" />
        <F label="सारांश (homepage card)" full textarea value={form.summary} on={(v) => setForm({ ...form, summary: v })} tid="f-summary" />
        <F label="विषयवस्तु" required full textarea rows={6} value={form.content} on={(v) => setForm({ ...form, content: v })} tid="f-content" />

        <div>
          <L>श्रेणी</L>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="f-category" className="mt-1.5 w-full px-4 py-3 rounded-xl border border-zinc-200">
            {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name_hi}</option>)}
          </select>
        </div>
        <F label="लेखक" value={form.author} on={(v) => setForm({ ...form, author: v })} tid="f-author" />

        <div className="sm:col-span-2">
          <L>कवर इमेज</L>
          <div className="mt-1.5 flex flex-wrap gap-3 items-center">
            {form.cover_image ? <img src={form.cover_image} alt="" className="h-20 w-32 object-cover rounded-lg border border-zinc-200" /> : null}
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="f-cover-upload" className="text-sm" />
            <input type="text" placeholder="या URL पेस्ट करें" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="f-cover-url" className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
          </div>
        </div>

        <F label="टैग (कॉमा से अलग)" value={form.tags} on={(v) => setForm({ ...form, tags: v })} tid="f-tags" />
        <F label="वीडियो / Embed URL (YouTube, Instagram, Facebook, Spotify, Twitter)" full value={form.video_url} on={(v) => setForm({ ...form, video_url: v })} tid="f-video" />
        {form.video_url && detectMedia(form.video_url) && (
          <div className="sm:col-span-2">
            <div className="flex items-center gap-2 mb-2 text-xs font-outfit font-bold uppercase tracking-widest text-zinc-500">
              <Eye className="h-3 w-3" /> Embed Preview
            </div>
            <MediaEmbed url={form.video_url} className="mt-0" />
          </div>
        )}
        <F label="ऑडियो URL (Podcast / MP3) — Podcast एपिसोड के लिए" full value={form.audio_url} on={(v) => setForm({ ...form, audio_url: v })} tid="f-audio" />
        {form.audio_url && (
          <div className="sm:col-span-2">
            <audio controls src={form.audio_url} className="w-full" data-testid="f-audio-preview" />
          </div>
        )}
        <F label="प्राथमिकता (Priority)" type="number" value={form.priority} on={(v) => setForm({ ...form, priority: v })} tid="f-priority" />

        <div className="sm:col-span-2 flex flex-wrap gap-3 pt-2">
          {[["is_breaking","Breaking"],["is_trending","Trending"],["is_featured","Featured"],["is_short","Short"],["is_video","Video"]].map(([k, label]) => (
            <label key={k} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-50 border border-zinc-200 cursor-pointer">
              <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} data-testid={`f-${k}`} />
              <span className="text-sm font-outfit font-bold">{label}</span>
            </label>
          ))}
        </div>

        <div className="sm:col-span-2 mt-4 border-t border-zinc-100 pt-5">
          <h3 className="font-devanagari font-bold mb-3">वर्कफ्लो</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <L>स्थिति</L>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="f-status" className="mt-1.5 w-full px-4 py-3 rounded-xl border border-zinc-200">
                {statusOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {form.status === "scheduled" && (
              <div>
                <L>प्रकाशन समय</L>
                <input type="datetime-local" value={form.publish_at} onChange={(e) => setForm({ ...form, publish_at: e.target.value })} data-testid="f-publish-at" className="mt-1.5 w-full px-4 py-3 rounded-xl border border-zinc-200" />
              </div>
            )}
          </div>
        </div>

        <div className="sm:col-span-2 mt-4 border-t border-zinc-100 pt-5">
          <h3 className="font-devanagari font-bold mb-3">SEO</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Meta Title" value={form.meta_title} on={(v) => setForm({ ...form, meta_title: v })} tid="f-meta-title" />
            <F label="Focus Keyword" value={form.focus_keyword} on={(v) => setForm({ ...form, focus_keyword: v })} tid="f-focus" />
            <F label="Meta Description" full textarea value={form.meta_description} on={(v) => setForm({ ...form, meta_description: v })} tid="f-meta-desc" />
            <F label="OG Image URL" value={form.og_image} on={(v) => setForm({ ...form, og_image: v })} tid="f-og" />
            <F label="Custom Slug (optional)" value={form.slug_override} on={(v) => setForm({ ...form, slug_override: v })} tid="f-slug" />
          </div>
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button type="submit" data-testid="article-save" className="bodh-gradient text-white font-outfit font-bold px-6 py-2.5 rounded-full">सेव करें</button>
        <button type="button" onClick={onCancel} className="border border-zinc-200 text-zinc-700 font-outfit font-bold px-6 py-2.5 rounded-full">रद्द करें</button>
      </div>
    </form>
  );
}

// ============ MEDIA ============
function MediaTab() {
  const [items, setItems] = useState([]);
  const fileRef = useRef();
  const refresh = () => api.get("/media").then((r) => setItems(r.data || []));
  useEffect(() => { refresh(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("अपलोड सफल");
      refresh();
    } catch { toast.error("अपलोड विफल"); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const del = async (id) => {
    if (!window.confirm("हटाना चाहते हैं?")) return;
    await api.delete(`/media/${id}`); refresh();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-devanagari font-black text-2xl">मीडिया लाइब्रेरी</h2>
        <label className="inline-flex items-center gap-1.5 bodh-gradient text-white font-outfit font-bold px-5 py-2.5 rounded-full cursor-pointer">
          <UploadCloud className="h-4 w-4" /> अपलोड
          <input ref={fileRef} type="file" accept="image/*,video/*,audio/*" onChange={upload} className="hidden" data-testid="media-upload" />
        </label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="media-grid">
        {items.map((m) => {
          const url = `${process.env.REACT_APP_BACKEND_URL}${m.url}`;
          const isImg = m.content_type?.startsWith("image/");
          return (
            <div key={m.id} className="relative rounded-2xl border border-zinc-100 overflow-hidden group" data-testid={`media-${m.id}`}>
              {isImg ? (
                <img src={url} alt={m.original} className="w-full h-32 object-cover" />
              ) : (
                <div className="w-full h-32 flex items-center justify-center bg-zinc-100 text-zinc-500 text-xs">{m.content_type}</div>
              )}
              <div className="p-2 text-[10px] font-mono truncate text-zinc-500">{m.original}</div>
              <button onClick={() => { navigator.clipboard.writeText(url); toast.success("URL कॉपी"); }} className="absolute bottom-2 left-2 text-[10px] bg-white/90 px-2 py-0.5 rounded">Copy URL</button>
              <button onClick={() => del(m.id)} data-testid={`media-del-${m.id}`} className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"><Trash2 className="h-3.5 w-3.5 text-[#E30613]" /></button>
            </div>
          );
        })}
        {items.length === 0 && <div className="col-span-full text-center font-devanagari text-zinc-400 py-10">अभी कोई मीडिया नहीं</div>}
      </div>
    </div>
  );
}

// ============ CATEGORIES ============
function CategoriesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slug: "", name_hi: "", name_en: "", enabled: true, order: 99 });
  const refresh = () => api.get("/categories", { params: { include_disabled: true } }).then((r) => setItems(r.data || []));
  useEffect(() => { refresh(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/categories/${editing}`, form);
      else await api.post("/categories", form);
      toast.success("सेव हुआ");
      setEditing(null); setForm({ slug: "", name_hi: "", name_en: "", enabled: true, order: 99 });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "त्रुटि"); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <form onSubmit={save} className="lg:col-span-4 bg-white rounded-2xl border border-zinc-100 p-5" data-testid="category-form">
        <h3 className="font-devanagari font-black text-lg mb-4">{editing ? "श्रेणी संपादित करें" : "नई श्रेणी"}</h3>
        <F label="Slug (अंग्रेजी)" required value={form.slug} on={(v) => setForm({ ...form, slug: v })} tid="cf-slug" />
        <F label="नाम (हिंदी)" required value={form.name_hi} on={(v) => setForm({ ...form, name_hi: v })} tid="cf-hi" />
        <F label="Name (English)" required value={form.name_en} on={(v) => setForm({ ...form, name_en: v })} tid="cf-en" />
        <F label="Order" type="number" value={form.order} on={(v) => setForm({ ...form, order: Number(v) })} tid="cf-order" />
        <label className="flex items-center gap-2 mt-3"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="cf-enabled" /> <span className="text-sm font-outfit font-bold">Enabled</span></label>
        <div className="mt-4 flex gap-2">
          <button type="submit" data-testid="cat-save" className="bodh-gradient text-white font-outfit font-bold px-5 py-2 rounded-full">{editing ? "अपडेट" : "जोडें"}</button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm({ slug: "", name_hi: "", name_en: "", enabled: true, order: 99 }); }} className="border border-zinc-200 px-5 py-2 rounded-full font-outfit font-bold">रद्द</button>}
        </div>
      </form>
      <div className="lg:col-span-8 bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left font-outfit text-xs uppercase tracking-widest text-zinc-500">
              <th className="px-4 py-3">हिंदी नाम</th><th className="px-4 py-3">EN</th><th className="px-4 py-3">Slug</th><th className="px-4 py-3">क्रम</th><th className="px-4 py-3">स्थिति</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.sort((a,b)=>a.order-b.order).map((c) => (
              <tr key={c.slug} className="border-b border-zinc-50" data-testid={`cat-row-${c.slug}`}>
                <td className="px-4 py-2.5 font-devanagari font-bold">{c.name_hi}</td>
                <td className="px-4 py-2.5">{c.name_en}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{c.slug}</td>
                <td className="px-4 py-2.5">{c.order}</td>
                <td className="px-4 py-2.5">{c.enabled ? <span className="text-green-700 text-xs font-bold">सक्रिय</span> : <span className="text-zinc-400 text-xs">निष्क्रिय</span>}</td>
                <td className="px-4 py-2.5 text-right"><button onClick={() => { setEditing(c.slug); setForm(c); }} data-testid={`cat-edit-${c.slug}`} className="text-zinc-700 hover:text-[#E30613]"><Pencil className="h-3.5 w-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ PAGES ============
function PagesTab() {
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ title: "", subtitle: "", content: "" });
  const refresh = () => api.get("/pages").then((r) => setPages(r.data || []));
  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    if (active) {
      const p = pages.find((x) => x.slug === active);
      if (p) setForm({ title: p.title, subtitle: p.subtitle || "", content: p.content || "" });
    }
  }, [active, pages]);

  const save = async (e) => {
    e.preventDefault();
    try { await api.put(`/pages/${active}`, form); toast.success("पेज अपडेट हुआ"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "त्रुटि"); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-3 bg-white rounded-2xl border border-zinc-100 p-3">
        <ul>
          {pages.map((p) => (
            <li key={p.slug}>
              <button onClick={() => setActive(p.slug)} data-testid={`page-select-${p.slug}`} className={`w-full text-left px-3 py-2 rounded-lg text-sm font-devanagari font-semibold flex items-center justify-between ${active === p.slug ? "bodh-gradient text-white" : "hover:bg-zinc-50"}`}>
                {p.title} <ChevronRight className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-9">
        {!active ? (
          <div className="bg-white rounded-2xl border border-zinc-100 p-10 text-center text-zinc-400 font-devanagari">बाएँ से कोई पेज चुनें</div>
        ) : (
          <form onSubmit={save} className="bg-white rounded-2xl border border-zinc-100 p-6" data-testid="page-form">
            <div className="text-xs font-mono text-zinc-500 mb-2">/{active}</div>
            <F label="शीर्षक" required full value={form.title} on={(v) => setForm({ ...form, title: v })} tid="pf-title" />
            <F label="उपशीर्षक" full value={form.subtitle} on={(v) => setForm({ ...form, subtitle: v })} tid="pf-subtitle" />
            <F label="कंटेंट" full textarea rows={12} value={form.content} on={(v) => setForm({ ...form, content: v })} tid="pf-content" />
            <button type="submit" data-testid="page-save" className="mt-4 bodh-gradient text-white font-outfit font-bold px-6 py-2.5 rounded-full">सेव करें</button>
          </form>
        )}
      </div>
    </div>
  );
}

// ============ USERS ============
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "reporter" });
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const csvRef = useRef();
  const refresh = () => api.get("/admin/users").then((r) => setUsers(r.data || []));
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/users", form); toast.success("यूजर बनाया गया"); setForm({ name: "", email: "", password: "", role: "reporter" }); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "त्रुटि"); }
  };

  const update = async (id, patch) => {
    try { await api.put(`/admin/users/${id}`, patch); toast.success("अपडेट"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "त्रुटि"); }
  };

  const del = async (id) => {
    if (!window.confirm("यूजर हटाएँ?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("हटाया गया"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "त्रुटि"); }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvBusy(true);
    setCsvResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/users/bulk-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvResult(data);
      toast.success(`✓ ${data.summary.created} नए, ${data.summary.updated} अपडेट, ${data.summary.errors} त्रुटि`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV अपलोड विफल");
    } finally {
      setCsvBusy(false);
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  const downloadCsvSample = () => {
    const csv = "name,email,role,password\nPriya Sharma,priya@example.com,reporter,\nRavi Kumar,ravi@example.com,editor,OptionalPass2026\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "bodh-writers-template.csv";
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Bulk CSV onboarding strip */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100 p-5" data-testid="bulk-csv-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-devanagari font-black text-lg flex items-center gap-2">
              <FileUp className="h-4 w-4 text-purple-700" /> बल्क CSV से लेखक जोडें
            </h3>
            <p className="text-xs text-zinc-600 font-hind mt-1 max-w-2xl">
              CSV कॉलम: <code className="text-purple-700">name, email, role, password</code> (पासवर्ड वैकल्पिक — खाली रहने पर ऑटो-जेनरेट होगा).
              पुनः अपलोड पर मौजूदा ईमेल अपडेट होंगे (idempotent).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadCsvSample} data-testid="csv-sample-download" className="px-3 py-1.5 rounded-full text-xs font-outfit font-bold border border-purple-200 text-purple-700 hover:bg-purple-100 transition">
              📥 सैंपल CSV
            </button>
            <label className="px-3 py-1.5 rounded-full text-xs font-outfit font-bold bg-purple-600 text-white hover:bg-purple-700 cursor-pointer transition inline-flex items-center gap-1.5">
              <FileUp className="h-3.5 w-3.5" />
              {csvBusy ? "Uploading..." : "CSV अपलोड करें"}
              <input ref={csvRef} type="file" accept=".csv,text/csv" onChange={uploadCsv} disabled={csvBusy} data-testid="csv-upload" className="hidden" />
            </label>
          </div>
        </div>

        {csvResult && (
          <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs font-hind" data-testid="csv-result">
            <div className="bg-white/70 rounded-xl p-3 border border-green-100">
              <div className="font-outfit font-black text-green-700 uppercase text-[10px] tracking-widest">Created ({csvResult.summary.created})</div>
              {csvResult.created.slice(0, 5).map((c, i) => (
                <div key={i} className="mt-1 text-zinc-700">
                  {c.email} <span className="text-zinc-400">({c.role})</span>
                  {c.temp_password && <div className="text-[10px] font-mono text-purple-700">🔑 {c.temp_password}</div>}
                </div>
              ))}
              {csvResult.created.length > 5 && <div className="mt-1 text-zinc-400">+ {csvResult.created.length - 5} more</div>}
            </div>
            <div className="bg-white/70 rounded-xl p-3 border border-blue-100">
              <div className="font-outfit font-black text-blue-700 uppercase text-[10px] tracking-widest">Updated ({csvResult.summary.updated})</div>
              {csvResult.updated.slice(0, 5).map((u, i) => <div key={i} className="mt-1 text-zinc-700">{u.email}</div>)}
            </div>
            <div className="bg-white/70 rounded-xl p-3 border border-red-100">
              <div className="font-outfit font-black text-red-700 uppercase text-[10px] tracking-widest">Errors ({csvResult.summary.errors})</div>
              {csvResult.errors.slice(0, 5).map((e, i) => <div key={i} className="mt-1 text-zinc-700">Row {e.row}: {e.error}</div>)}
            </div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
      <form onSubmit={create} className="lg:col-span-4 bg-white rounded-2xl border border-zinc-100 p-5" data-testid="user-form">
        <h3 className="font-devanagari font-black text-lg mb-4">नया यूजर आमंत्रित करें</h3>
        <F label="नाम" required value={form.name} on={(v) => setForm({ ...form, name: v })} tid="uf-name" />
        <F label="ईमेल" type="email" required value={form.email} on={(v) => setForm({ ...form, email: v })} tid="uf-email" />
        <F label="पासवर्ड" type="password" required value={form.password} on={(v) => setForm({ ...form, password: v })} tid="uf-pwd" />
        <div className="mt-2">
          <L>भूमिका</L>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="uf-role" className="mt-1.5 w-full px-4 py-3 rounded-xl border border-zinc-200">
            <option value="reporter">Reporter</option>
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <button type="submit" data-testid="user-create" className="mt-4 bodh-gradient text-white font-outfit font-bold px-5 py-2 rounded-full">आमंत्रित करें</button>
      </form>

      <div className="lg:col-span-8 bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left font-outfit text-xs uppercase tracking-widest text-zinc-500">
              <th className="px-4 py-3">नाम</th><th className="px-4 py-3">ईमेल</th><th className="px-4 py-3">भूमिका</th><th className="px-4 py-3">स्थिति</th><th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-zinc-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-2.5 font-devanagari font-bold">{u.name}</td>
                <td className="px-4 py-2.5 text-xs text-zinc-600">{u.email}</td>
                <td className="px-4 py-2.5">
                  <select value={u.role} onChange={(e) => update(u.id, { role: e.target.value })} data-testid={`urole-${u.id}`} className="text-xs px-2 py-1 rounded border border-zinc-200">
                    <option value="user">user</option><option value="reporter">reporter</option><option value="editor">editor</option><option value="admin">admin</option>
                  </select>
                </td>
                <td className="px-4 py-2.5">
                  <button onClick={() => update(u.id, { disabled: !u.disabled })} data-testid={`utoggle-${u.id}`} className={`text-xs font-bold px-2 py-1 rounded ${u.disabled ? "bg-zinc-200 text-zinc-600" : "bg-green-50 text-green-700"}`}>
                    {u.disabled ? "Disabled" : "Active"}
                  </button>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => del(u.id)} data-testid={`udel-${u.id}`} className="text-zinc-700 hover:text-[#E30613]"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

// ============ SETTINGS ============
function SettingsTab() {
  const [s, setS] = useState({
    site_name: "", tagline: "",
    social: { instagram: "", instagram_handle: "", youtube: "", youtube_handle: "", twitter: "", facebook: "", telegram: "" },
    contact: { email: "", ads_email: "", careers_email: "", phone: "", address: "" },
    live: { youtube_channel_id: "", youtube_live_video_id: "", enabled: false },
    broadcast: { whatsapp_template: "", default_hashtags: "" },
  });
  useEffect(() => {
    api.get("/settings").then((r) => setS((cur) => ({
      ...cur,
      ...r.data,
      social: { ...cur.social, ...(r.data?.social || {}) },
      contact: { ...cur.contact, ...(r.data?.contact || {}) },
      live: { ...cur.live, ...(r.data?.live || {}) },
      broadcast: { ...cur.broadcast, ...(r.data?.broadcast || {}) },
    })));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/settings", s); toast.success("सेटिंग्स सेव हो गईं"); }
    catch (e) { toast.error(e?.response?.data?.detail || "त्रुटि"); }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-zinc-100 p-6 space-y-8" data-testid="settings-form">
      <Section title="साइट">
        <F label="साइट नाम" value={s.site_name} on={(v) => setS({ ...s, site_name: v })} tid="st-name" />
        <F label="टैगलाइन" value={s.tagline} on={(v) => setS({ ...s, tagline: v })} tid="st-tagline" />
      </Section>

      <Section title="सोशल मीडिया">
        <F label="Instagram URL" value={s.social.instagram} on={(v) => setS({ ...s, social: { ...s.social, instagram: v } })} tid="st-ig" />
        <F label="Instagram Handle" value={s.social.instagram_handle} on={(v) => setS({ ...s, social: { ...s.social, instagram_handle: v } })} tid="st-ig-h" />
        <F label="YouTube URL" value={s.social.youtube} on={(v) => setS({ ...s, social: { ...s.social, youtube: v } })} tid="st-yt" />
        <F label="YouTube Handle" value={s.social.youtube_handle} on={(v) => setS({ ...s, social: { ...s.social, youtube_handle: v } })} tid="st-yt-h" />
        <F label="Twitter / X URL" value={s.social.twitter} on={(v) => setS({ ...s, social: { ...s.social, twitter: v } })} tid="st-tw" />
        <F label="Facebook URL" value={s.social.facebook} on={(v) => setS({ ...s, social: { ...s.social, facebook: v } })} tid="st-fb" />
        <F label="Telegram URL" value={s.social.telegram} on={(v) => setS({ ...s, social: { ...s.social, telegram: v } })} tid="st-tg" />
      </Section>

      <Section title="संपर्क / पता">
        <F label="संपर्क ईमेल" value={s.contact.email} on={(v) => setS({ ...s, contact: { ...s.contact, email: v } })} tid="st-ce" />
        <F label="विज्ञापन ईमेल" value={s.contact.ads_email} on={(v) => setS({ ...s, contact: { ...s.contact, ads_email: v } })} tid="st-ae" />
        <F label="करियर ईमेल" value={s.contact.careers_email} on={(v) => setS({ ...s, contact: { ...s.contact, careers_email: v } })} tid="st-cre" />
        <F label="फोन" value={s.contact.phone} on={(v) => setS({ ...s, contact: { ...s.contact, phone: v } })} tid="st-ph" />
        <F label="पता" full value={s.contact.address} on={(v) => setS({ ...s, contact: { ...s.contact, address: v } })} tid="st-addr" />
      </Section>

      <Section title="लाइव / YouTube">
        <F label="YouTube Channel ID" value={s.live.youtube_channel_id} on={(v) => setS({ ...s, live: { ...s.live, youtube_channel_id: v } })} tid="st-ytc" />
        <F label="YouTube Live Video ID" value={s.live.youtube_live_video_id} on={(v) => setS({ ...s, live: { ...s.live, youtube_live_video_id: v } })} tid="st-ytv" />
        <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={!!s.live.enabled} onChange={(e) => setS({ ...s, live: { ...s.live, enabled: e.target.checked } })} data-testid="st-yten" /> <span className="text-sm font-outfit font-bold">Live Enabled</span></label>
      </Section>

      <Section title="WhatsApp Broadcast टेम्पलेट">
        <F label="WhatsApp Message Template" full textarea rows={7}
           value={s.broadcast.whatsapp_template}
           on={(v) => setS({ ...s, broadcast: { ...s.broadcast, whatsapp_template: v } })}
           tid="st-wa-tpl" />
        <F label="Default Hashtags" full
           value={s.broadcast.default_hashtags}
           on={(v) => setS({ ...s, broadcast: { ...s.broadcast, default_hashtags: v } })}
           tid="st-wa-tags" />
        <div className="sm:col-span-2 p-3 rounded-xl bg-zinc-50 border border-zinc-100 text-xs font-hind text-zinc-600">
          <b>Placeholders:</b> <code className="font-mono">{"{title}"}</code> · <code className="font-mono">{"{summary}"}</code> · <code className="font-mono">{"{url}"}</code> · <code className="font-mono">{"{category}"}</code> · <code className="font-mono">{"{hashtags}"}</code> · <code className="font-mono">{"{author}"}</code>
        </div>
      </Section>

      <button type="submit" data-testid="settings-save" className="bodh-gradient text-white font-outfit font-bold px-6 py-2.5 rounded-full">सेव करें</button>
    </form>
  );
}

// ============ helpers ============
function L({ children }) {
  return <label className="text-xs font-outfit font-bold uppercase tracking-widest text-zinc-500">{children}</label>;
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="font-devanagari font-black text-lg mb-3">{title}</h3>
      <div className="grid sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function F({ label, value, on, type = "text", textarea, rows = 2, tid, full, required }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <L>{label}</L>
      {textarea ? (
        <textarea required={required} rows={rows} value={value || ""} onChange={(e) => on(e.target.value)} data-testid={tid} className="mt-1.5 w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-[#E30613] outline-none font-devanagari resize-y" />
      ) : (
        <input required={required} type={type} value={value ?? ""} onChange={(e) => on(e.target.value)} data-testid={tid} className="mt-1.5 w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-[#E30613] outline-none font-devanagari" />
      )}
    </div>
  );
}


// ============ WIRE SOURCES (RSS / Atom ingestion) ============
const EMPTY_SOURCE = {
  name: "", url: "", category: "national", enabled: false,
  is_breaking: false, auto_publish: false,
  fetch_interval_minutes: 15, max_items_per_poll: 15, author_override: "",
};

function SourcesTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SOURCE);
  const [polling, setPolling] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/sources");
      setItems(data || []);
    } catch (e) {
      toast.error("Failed to load sources");
    }
  };
  useEffect(() => { refresh(); }, []);

  const startNew = () => { setEditing(null); setForm(EMPTY_SOURCE); setShowForm(true); };
  const startEdit = (s) => { setEditing(s.id); setForm({ ...EMPTY_SOURCE, ...s }); setShowForm(true); };
  const cancel = () => { setShowForm(false); setEditing(null); setForm(EMPTY_SOURCE); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      fetch_interval_minutes: Number(form.fetch_interval_minutes) || 15,
      max_items_per_poll: Number(form.max_items_per_poll) || 15,
    };
    try {
      if (editing) await api.put(`/admin/sources/${editing}`, payload);
      else await api.post("/admin/sources", payload);
      toast.success(editing ? "अपडेट हुआ" : "सोर्स जोडा गया");
      cancel(); refresh();
    } catch (e) {
      toast.error("त्रुटि: " + (e?.response?.data?.detail || "save failed"));
    }
  };

  const del = async (id, name) => {
    if (!window.confirm(`हटाएँ "${name}"?`)) return;
    try { await api.delete(`/admin/sources/${id}`); toast.success("हटाया गया"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "त्रुटि"); }
  };

  const pollNow = async (s) => {
    setPolling(s.id);
    try {
      const { data } = await api.post(`/admin/sources/${s.id}/poll`);
      if (data.ok) toast.success(`✓ ${data.imported} नई खबरें (${data.skipped} duplicate)`);
      else toast.error(`Poll failed: ${data.error || "unknown"}`);
      refresh();
    } catch (e) {
      toast.error("Poll error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPolling(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-devanagari font-black text-2xl text-zinc-900">वायर सोर्स</h2>
          <p className="text-sm text-zinc-500 font-hind mt-0.5">RSS/Atom फ़ीड से ख़बरें ऑटो-इम्पोर्ट. नई आइटम <strong>समीक्षा हेतु</strong> कतार में आती हैं.</p>
        </div>
        <button onClick={startNew} data-testid="source-new" className="bodh-gradient text-white font-outfit font-bold px-4 py-2 rounded-full inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> नया सोर्स
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-2xl border border-zinc-100 p-5 mb-6 grid sm:grid-cols-2 gap-4" data-testid="source-form">
          <F label="नाम" value={form.name} on={(v) => setForm({ ...form, name: v })} tid="sf-name" required />
          <F label="RSS / Atom URL" value={form.url} on={(v) => setForm({ ...form, url: v })} tid="sf-url" required />
          <div>
            <L>श्रेणी</L>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="sf-category" className="mt-1.5 w-full px-4 py-3 rounded-xl border border-zinc-200">
              {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name_hi}</option>)}
            </select>
          </div>
          <F label="लेखक नाम (वैकल्पिक)" value={form.author_override} on={(v) => setForm({ ...form, author_override: v })} tid="sf-author" />
          <F label="अंतराल (मिनट)" type="number" value={form.fetch_interval_minutes} on={(v) => setForm({ ...form, fetch_interval_minutes: v })} tid="sf-interval" />
          <F label="प्रति पोल अधिकतम आइटम" type="number" value={form.max_items_per_poll} on={(v) => setForm({ ...form, max_items_per_poll: v })} tid="sf-max" />

          <div className="sm:col-span-2 flex flex-wrap gap-3 pt-2">
            {[["enabled","सक्रिय"],["is_breaking","Breaking मार्क"],["auto_publish","ऑटो-प्रकाशित (समीक्षा छोडें)"]].map(([k, label]) => (
              <label key={k} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-50 border border-zinc-200 cursor-pointer">
                <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} data-testid={`sf-${k}`} />
                <span className="text-sm font-outfit font-bold">{label}</span>
              </label>
            ))}
          </div>
          <div className="sm:col-span-2 flex gap-3 pt-2">
            <button type="submit" data-testid="source-save" className="bodh-gradient text-white font-outfit font-bold px-6 py-2.5 rounded-full">सेव करें</button>
            <button type="button" onClick={cancel} data-testid="source-cancel" className="px-6 py-2.5 rounded-full border border-zinc-200 font-outfit font-bold">रद्द</button>
          </div>
        </form>
      )}

      <div className="space-y-3" data-testid="sources-list">
        {items.length === 0 && (
          <div className="text-center py-12 text-zinc-400 font-hind border border-dashed border-zinc-200 rounded-2xl">
            कोई सोर्स नहीं — पहला RSS सोर्स जोडें.
          </div>
        )}
        {items.map((s) => (
          <div key={s.id} data-testid={`source-row-${s.id}`} className="bg-white rounded-2xl border border-zinc-100 p-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-devanagari font-bold text-zinc-900">{s.name}</h3>
                {s.enabled
                  ? <span className="text-[10px] font-outfit font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-green-50 text-green-700">ACTIVE</span>
                  : <span className="text-[10px] font-outfit font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">PAUSED</span>}
                {s.is_breaking && <span className="text-[10px] font-outfit font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-50 text-red-700">BREAKING</span>}
                {s.auto_publish && <span className="text-[10px] font-outfit font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">AUTO-PUBLISH</span>}
              </div>
              <a href={s.url} target="_blank" rel="noreferrer" className="text-xs text-zinc-500 hover:text-[#E30613] truncate block max-w-md">{s.url}</a>
              <div className="text-xs text-zinc-400 mt-1 font-hind">
                श्रेणी: <strong>{s.category}</strong> • अंतराल: {s.fetch_interval_minutes}m • कुल इम्पोर्ट: <strong>{s.items_imported}</strong>
                {s.last_polled_at && <> • अंतिम: {new Date(s.last_polled_at).toLocaleString("hi-IN")}</>}
              </div>
              {s.last_status && <div className="text-[11px] text-zinc-400 mt-0.5 font-mono">{s.last_status}</div>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => pollNow(s)} disabled={polling === s.id} data-testid={`source-poll-${s.id}`}
                className="h-9 px-3 rounded-full bodh-gradient text-white text-xs font-outfit font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
                {polling === s.id ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" />Polling</> : <><Zap className="h-3.5 w-3.5" />Poll Now</>}
              </button>
              <button onClick={() => startEdit(s)} data-testid={`source-edit-${s.id}`} className="h-9 w-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bg-zinc-50"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(s.id, s.name)} data-testid={`source-del-${s.id}`} className="h-9 w-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ ANALYTICS ============
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/analytics");
      setData(data);
    } catch (e) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="text-center py-20 font-devanagari text-zinc-400" data-testid="analytics-loading">Loading analytics...</div>;
  }
  if (!data) {
    return <div className="text-center py-20 font-devanagari text-zinc-400">No data</div>;
  }

  const t = data.totals;
  const series = data.timeseries_7d || [];
  const maxView = Math.max(1, ...series.map((d) => d.views));

  const KPI = ({ icon: Icon, label, value, color = "text-zinc-900", testid }) => (
    <div data-testid={testid} className="bg-white rounded-2xl border border-zinc-100 p-5 hover:shadow-md transition">
      <div className="flex items-center gap-2 text-xs font-outfit font-black uppercase tracking-widest text-zinc-400">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`mt-2 font-outfit font-black text-3xl ${color}`}>{value}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-devanagari font-black text-2xl text-zinc-900">एनालिटिक्स</h2>
          <p className="text-sm text-zinc-500 font-hind mt-0.5">पाठक रुझान, कंटेंट हेल्थ, और वायर इंजेस्ट की रियल-टाइम स्थिति</p>
        </div>
        <button
          onClick={() => { setRefreshing(true); load(); }}
          disabled={refreshing}
          data-testid="analytics-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-outfit font-bold border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={Eye} label="कुल व्यूज़" value={t.total_views.toLocaleString("hi-IN")} color="bodh-text-gradient" testid="kpi-total-views" />
        <KPI icon={Activity} label="आज" value={t.today_views.toLocaleString("hi-IN")} testid="kpi-today-views" />
        <KPI icon={Users} label="आज unique" value={t.today_uniques.toLocaleString("hi-IN")} testid="kpi-today-uniques" />
        <KPI icon={FileText} label="प्रकाशित" value={t.published_articles.toLocaleString("hi-IN")} testid="kpi-published" />
        <KPI icon={Clock} label="समीक्षा कतार" value={t.submitted_queue.toLocaleString("hi-IN")} color="text-amber-700" testid="kpi-submitted" />
        <KPI icon={Clock} label="शेड्यूल्ड" value={t.scheduled.toLocaleString("hi-IN")} color="text-blue-700" testid="kpi-scheduled" />
        <KPI icon={Pencil} label="ड्राफ्ट" value={t.drafts.toLocaleString("hi-IN")} color="text-zinc-600" testid="kpi-drafts" />
        <KPI icon={Rss} label="वायर 24h" value={t.wire_imported_24h.toLocaleString("hi-IN")} color="text-purple-700" testid="kpi-wire-24h" />
      </div>

      {/* 7-day chart */}
      <div className="bg-white rounded-2xl border border-zinc-100 p-5" data-testid="analytics-7d-chart">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-devanagari font-bold text-lg">पिछले 7 दिन — व्यूज़</h3>
          <span className="text-xs text-zinc-400 font-outfit">Peak: {maxView.toLocaleString("hi-IN")}</span>
        </div>
        <div className="flex items-end gap-2 h-40">
          {series.map((d) => {
            const h = Math.max(4, (d.views / maxView) * 140);
            const label = new Date(d.day).toLocaleDateString("hi-IN", { day: "numeric", month: "short" });
            return (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`bar-${d.day}`}>
                <div className="text-[10px] font-outfit font-bold text-zinc-600">{d.views}</div>
                <div
                  className="w-full bodh-gradient rounded-t-md transition-all hover:opacity-80"
                  style={{ height: `${h}px` }}
                />
                <div className="text-[10px] font-hind text-zinc-400">{label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top articles + category split */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-zinc-100 overflow-hidden" data-testid="top-articles-card">
          <div className="px-5 py-4 border-b border-zinc-100">
            <h3 className="font-devanagari font-bold text-lg flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#E30613]" /> टॉप 10 खबरें (सर्वकालिक)
            </h3>
          </div>
          {data.top_articles?.length ? (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left font-outfit text-xs uppercase tracking-widest text-zinc-500">
                <tr><th className="px-4 py-2">#</th><th className="px-4 py-2">शीर्षक</th><th className="px-4 py-2">श्रेणी</th><th className="px-4 py-2 text-right">व्यूज़</th></tr>
              </thead>
              <tbody>
                {data.top_articles.map((a, i) => (
                  <tr key={a.id} data-testid={`top-row-${a.id}`} className="border-t border-zinc-50">
                    <td className="px-4 py-2.5 font-outfit font-black text-[#E30613] w-8">{i + 1}</td>
                    <td className="px-4 py-2.5">
                      <Link to={`/article/${a.slug}`} className="font-devanagari font-semibold hover:text-[#E30613] line-clamp-1">{a.title}</Link>
                      <div className="text-[11px] text-zinc-400">{a.author}</div>
                    </td>
                    <td className="px-4 py-2.5 font-devanagari text-zinc-600 text-xs">{CATEGORIES.find((c) => c.slug === a.category)?.name_hi || a.category}</td>
                    <td className="px-4 py-2.5 text-right font-outfit font-black text-zinc-900">{a.views.toLocaleString("hi-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-5 py-10 text-center text-zinc-400 font-hind">अभी कोई व्यूज़ नहीं</div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden" data-testid="category-today-card">
          <div className="px-5 py-4 border-b border-zinc-100">
            <h3 className="font-devanagari font-bold text-lg flex items-center gap-2">
              <FolderTree className="h-4 w-4 text-[#E30613]" /> श्रेणीवार — आज
            </h3>
          </div>
          {data.category_today?.length ? (
            <div className="p-5 space-y-3">
              {data.category_today.map((c) => {
                const maxC = Math.max(1, ...data.category_today.map((x) => x.views));
                const pct = Math.round((c.views / maxC) * 100);
                const name = CATEGORIES.find((x) => x.slug === c.category)?.name_hi || c.category;
                return (
                  <div key={c.category} data-testid={`cat-row-${c.category}`}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-devanagari font-bold text-zinc-700">{name}</span>
                      <span className="font-outfit font-black text-zinc-900">{c.views}</span>
                    </div>
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full bodh-gradient" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-5 py-10 text-center text-zinc-400 font-hind text-sm">आज अभी कोई व्यू नहीं</div>
          )}
        </div>
      </div>
    </div>
  );
}

